/**
 * Product Link Shortcode Handler for Shopify
 * Direct API version - no JSON file dependencies
 * Processes [plink sku="..." alt="..."] shortcodes using Storefront GraphQL and Admin APIs
 */

class ShopifyProductLinkHandler {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 10 * 60 * 1000; // 10 minutes
    this.initializationPromise = null;
    
    // Storefront API configuration - get from theme settings
    this.storefrontToken = window.shopifyProductLinkConfig?.storefrontToken || null;
    this.apiUrl = '/api/2023-10/graphql.json';
    
    // Admin API configuration - get from theme settings
    this.adminToken = window.shopifyProductLinkConfig?.adminToken || null;
    this.shopDomain = window.shopifyProductLinkConfig?.shopDomain || null;
  }

  /**
   * Initialize the handler (detect content and set up processing)
   */
  async initialize() {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.detectAndProcessContent();
    return this.initializationPromise;
  }

  /**
   * Detect content containers and process shortcodes
   */
  async detectAndProcessContent() {
    const selectors = [
      '.article-content',
      '.rte',
      '.blog-article-content',
      '.page-content',
      '.content',
      '[data-content]'
    ];

    let found = false;
    for (const selector of selectors) {
      const containers = document.querySelectorAll(selector);
      if (containers.length > 0) {
        found = true;
        for (const container of containers) {
          await this.processShortcodes(container);
        }
      }
    }

    if (!found) {
      console.warn('No content containers found. Shortcodes may not be processed.');
    }
  }

  /**
   * Process shortcodes in a container element
   */
  async processShortcodes(container) {
    const shortcodeRegex = /\[plink\s+sku="([^"]+)"(?:\s+alt="([^"]*)")?\]/g;
    const textNodes = this.getTextNodes(container);
    
    for (const textNode of textNodes) {
      const originalText = textNode.textContent;
      let match;
      let hasMatches = false;
      let newHTML = originalText;

      // Reset regex lastIndex for each text node
      shortcodeRegex.lastIndex = 0;
      
      while ((match = shortcodeRegex.exec(originalText)) !== null) {
        hasMatches = true;
        const [fullMatch, sku, alt] = match;
        const params = { sku: sku.trim(), alt: alt || null };
        
        const product = await this.getProduct(params.sku);
        const replacement = this.renderProductLink(product, params);
        
        newHTML = newHTML.replace(fullMatch, replacement);
      }

      if (hasMatches) {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = newHTML;
        
        while (wrapper.firstChild) {
          textNode.parentNode.insertBefore(wrapper.firstChild, textNode);
        }
        textNode.remove();
      }
    }
  }

  /**
   * Get all text nodes within an element
   */
  getTextNodes(element) {
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: function(node) {
          // Skip nodes that are already inside product links or scripts
          if (node.parentElement.matches('a[class*="product-link"], script, style')) {
            return NodeFilter.FILTER_REJECT;
          }
          // Only include nodes with shortcode patterns
          return /\[plink\s+sku="[^"]+"/i.test(node.textContent) 
            ? NodeFilter.FILTER_ACCEPT 
            : NodeFilter.FILTER_REJECT;
        }
      }
    );

    const textNodes = [];
    let node;
    while (node = walker.nextNode()) {
      textNodes.push(node);
    }
    return textNodes;
  }

  /**
   * Get product information by SKU
   */
  async getProduct(sku) {
    const trimmedSku = sku.trim();
    const cacheKey = trimmedSku.toLowerCase();
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    // Use Shopify Storefront GraphQL API for direct product lookup
    if (this.storefrontToken === 'YOUR_STOREFRONT_ACCESS_TOKEN') {
      console.error('Storefront API token not configured');
      return null;
    }

    // GraphQL query to search products by SKU using the working pattern
    const query = `
      query searchProductsBySku($query: String!) {
        products(first: 50, query: $query) {
          edges {
            node {
              id
              title
              handle
              availableForSale
              variants(first: 50) {
                edges {
                  node {
                    id
                    sku
                    availableForSale
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      // Use the working search pattern - simple SKU without prefix
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken
        },
        body: JSON.stringify({
          query: query,
          variables: { 
            query: trimmedSku // Use trimmed SKU for consistent lookup
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(`GraphQL errors: ${result.errors.map(e => e.message).join(', ')}`);
      }

      const products = result.data?.products?.edges || [];

      // Find exact variant SKU match
      for (const productEdge of products) {
        const product = productEdge.node;
        
        for (const variantEdge of product.variants.edges) {
          const variant = variantEdge.node;
          
          if (variant.sku && variant.sku.trim().toLowerCase() === trimmedSku.toLowerCase()) {
            const productData = {
              title: product.title,
              url: `/products/${product.handle}`,
              sku: trimmedSku,
              status: 'active'
            };

            // Cache the result
            this.cache.set(cacheKey, {
              data: productData,
              timestamp: Date.now()
            });

            return productData;
          }
        }
      }

      // If not found in Storefront API, try Admin API for archived products
      const archivedProduct = await this.searchArchivedProducts(trimmedSku);
      if (archivedProduct) {
        // Cache the result
        this.cache.set(cacheKey, {
          data: archivedProduct,
          timestamp: Date.now()
        });
        return archivedProduct;
      }

      // No product found anywhere - cache the null result
      this.cache.set(cacheKey, {
        data: null,
        timestamp: Date.now()
      });

      return null;

    } catch (error) {
      console.error('Error in getProduct:', error);
      
      // Try archived products as fallback
      try {
        const archivedProduct = await this.searchArchivedProducts(trimmedSku);
        if (archivedProduct) {
          this.cache.set(cacheKey, {
            data: archivedProduct,
            timestamp: Date.now()
          });
          return archivedProduct;
        }
      } catch (archivedError) {
        console.error('Error searching archived products:', archivedError);
      }

      return null;
    }
  }

  /**
   * Search archived products using Admin API
   */
  async searchArchivedProducts(sku) {
    if (!this.adminToken || !this.shopDomain || this.adminToken.includes('YOUR_')) {
      return null;
    }

    try {
      const response = await fetch(`https://${this.shopDomain}/admin/api/2023-10/products.json?status=archived&limit=250`, {
        headers: {
          'X-Shopify-Access-Token': this.adminToken,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Admin API request failed: ${response.status}`);
      }

      const result = await response.json();
      const products = result.products || [];

      for (const product of products) {
        for (const variant of product.variants || []) {
          if (variant.sku === sku) {
            return {
              id: product.id,
              title: product.title,
              handle: product.handle,
              sku: sku,
              status: 'archived',
              url: `/products/${product.handle}`,
              availableForSale: false
            };
          }
        }
      }

      return null;

    } catch (error) {
      console.error('Error searching archived products:', error);
      return null;
    }
  }

  /**
   * Render product link HTML
   */
  renderProductLink(product, params) {
    if (!product) {
      // SKU not found in either API - erroneous SKU case
      if (params.alt) {
        return `<span class="product-nolink" title="Product SKU ${params.sku} not found">${params.alt}</span>`;
      } else {
        return `<span class="product-nolink" title="Product SKU ${params.sku} not found">[${params.sku} - SKU not found]</span>`;
      }
    }
    
    const linkText = params.alt || product.title;
    
    // Only active products get links - everything else gets spans
    if (product.status === 'active') {
      return `<a href="${product.url}" class="product-link-active" title="${product.title} (SKU: ${product.sku})" target="_blank">${linkText}</a>`;
    } else {
      return `<span class="product-nolink" title="${product.title} (SKU: ${product.sku}) - Product archived">${linkText}</span>`;
    }
  }

  /**
   * Test function for debugging
   */
  async testSku(sku) {
    const product = await this.getProduct(sku);
    return {
      sku: sku,
      product: product,
      found: !!product,
      status: product?.status || 'not found'
    };
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const handler = new ShopifyProductLinkHandler();
    handler.initialize().catch(error => {
      console.error('Product link handler initialization failed:', error);
    });
  });
} else {
  const handler = new ShopifyProductLinkHandler();
  handler.initialize().catch(error => {
    console.error('Product link handler initialization failed:', error);
  });
}

// Export for testing
if (typeof window !== 'undefined') {
  window.ShopifyProductLinkHandler = ShopifyProductLinkHandler;
  
  window.testSku = async (sku) => {
    if (!window.productLinkHandler) {
      window.productLinkHandler = new ShopifyProductLinkHandler();
    }
    return await window.productLinkHandler.testSku(sku);
  };
}
